import { TrendingUp, TrendingDown, DollarSign, Users, Zap, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { AnalyticsOverview } from "@/services/admin-api";

interface AnalyticsOverviewCardsProps {
  stats: AnalyticsOverview | undefined;
  isLoading: boolean;
  canViewRevenue: boolean;
}

export function AnalyticsOverviewCards({
  stats,
  isLoading,
  canViewRevenue,
}: AnalyticsOverviewCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : stats?.totalUsers.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">+{stats?.newUsersWeek || 0} this week</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : `${stats?.trialConversionRate || 0}%`}
          </div>
          <p className="text-xs text-muted-foreground">Trial to paid conversion</p>
        </CardContent>
      </Card>

      {canViewRevenue && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MRR</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${isLoading ? "..." : stats?.mrrCurrent.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">${stats?.revenueMonth || 0} this month</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">AI Usage</CardTitle>
          <Zap className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : (stats?.totalAiUsageMonth || 0).toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">This month</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
          <TrendingDown className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : `${stats?.churnRateMonth || 0}%`}
          </div>
          <p className="text-xs text-muted-foreground">Monthly churn</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Trials</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : stats?.totalTrialing.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            {stats?.totalFree || 0} Free, {stats?.totalPro || 0} Pro
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
